<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import Dialog from 'next/dialog/Dialog.vue';
import CommentTreeItem from './CommentTreeItem.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

const props = defineProps({
  show: { type: Boolean, default: false },
  messages: { type: Array, default: () => [] },
  highlightedMessageId: { type: Number, default: null },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const dialogRef = ref(null);

const topicMessage = computed(() => {
  return props.messages.find(m => m.private && !m.content_attributes?.deleted);
});

const topicContent = computed(() => {
  if (!topicMessage.value?.content) return '';
  return new MessageFormatter(topicMessage.value.content).formattedMessage;
});

watch(
  () => props.show,
  val => {
    if (val) {
      nextTick(() => dialogRef.value?.open());
    } else {
      dialogRef.value?.close();
    }
  }
);

function commentId(msg) {
  const ca = msg.content_attributes || {};
  return ca.facebook?.comment?.id || msg.source_id;
}

function parentCommentId(msg) {
  return (msg.content_attributes || {}).facebook?.comment?.fb_parent_id;
}

const treeNodes = computed(() => {
  const fbMessages = props.messages.filter(msg => {
    const ca = msg.content_attributes || {};
    return ca.type === 'facebook_comment' && !msg.private;
  });

  const byCommentId = new Map();
  const byId = new Map();
  fbMessages.forEach(msg => {
    const cid = commentId(msg);
    if (cid) byCommentId.set(cid, msg);
    byId.set(msg.id, msg);
  });

  const highlighted = byId.get(props.highlightedMessageId);
  if (!highlighted) return [];

  const ancestorIds = new Set();
  let current = highlighted;
  while (current) {
    ancestorIds.add(current.id);
    const pid = parentCommentId(current);
    current = pid ? byCommentId.get(pid) : null;
  }

  const highlightedParentCid = parentCommentId(highlighted);

  const relevantIds = new Set(ancestorIds);
  if (highlightedParentCid) {
    const highlightedIdx = fbMessages.findIndex(
      msg => msg.id === highlighted.id
    );
    fbMessages
      .slice(0, highlightedIdx + 1)
      .filter(msg => parentCommentId(msg) === highlightedParentCid)
      .forEach(msg => relevantIds.add(msg.id));
  }

  const childrenMap = new Map();
  const roots = [];

  fbMessages
    .filter(msg => relevantIds.has(msg.id))
    .forEach(msg => {
      const pid = parentCommentId(msg);
      if (
        pid &&
        byCommentId.has(pid) &&
        relevantIds.has(byCommentId.get(pid).id)
      ) {
        if (!childrenMap.has(pid)) childrenMap.set(pid, []);
        childrenMap.get(pid).push(msg);
      } else {
        roots.push(msg);
      }
    });

  const result = [];
  const visit = (msg, level) => {
    const cid = commentId(msg);
    result.push({ msg, indentLevel: level });
    const children = childrenMap.get(cid) || [];
    children.forEach(child => visit(child, Math.min(level + 1, 2)));
  };
  roots.forEach(root => visit(root, 0));

  return result;
});

function handleClose() {
  emit('close');
}

function handleItemClick(messageId) {
  emit('close');
  nextTick(() => {
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
  });
}

watch(
  () => props.show,
  async val => {
    if (!val) return;
    await nextTick();
    await nextTick();
    const el = document.querySelector('.comment-tree-highlighted');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="5xl"
    :title="t('CONVERSATION.COMMENT_TREE.TITLE')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
    @close="handleClose"
  >
    <div class="max-h-[80vh] overflow-y-auto flex flex-col gap-3 p-1">
      <div
        v-if="topicMessage"
        class="px-3 py-2 mb-2 rounded-lg bg-n-solid-amber text-sm text-n-amber-12"
      >
        <span
          v-if="topicContent"
          v-dompurify-html="topicContent"
          class="prose prose-bubble break-words"
        />
      </div>
      <div
        v-for="({ msg, indentLevel }, idx) in treeNodes"
        :key="msg.id || idx"
        :style="{ paddingLeft: indentLevel * 32 + 'px' }"
      >
        <CommentTreeItem :message="msg" @click="handleItemClick" />
      </div>
      <div
        v-if="treeNodes.length === 0"
        class="text-center text-n-slate-11 py-4"
      >
        {{ t('CONVERSATION.COMMENT_TREE.EMPTY') }}
      </div>
    </div>
  </Dialog>
</template>
